import { EntryLink } from '@/components/entry-link';
import { Button } from '@/components/ui/button';
import { useLocalizedRoute } from '@/hooks/use-localized-route';
import { useAsset } from '@/utils/asset';
import type { DotLottie } from '@lottiefiles/dotlottie-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type LottiePlayer = typeof import('@lottiefiles/dotlottie-react').DotLottieReact;

const STEPS = [
    { step: 1, src: 'animations/hk-step1animation.lottie', poster: 'images/how-it-works/step-1.svg' },
    { step: 2, src: 'animations/ident.lottie', poster: 'images/how-it-works/step-2.svg' },
    { step: 3, src: 'animations/bankTransfer.lottie', poster: 'images/how-it-works/step-3.svg' },
    { step: 4, src: 'animations/dashboard.lottie', poster: 'images/how-it-works/step-4.svg' },
] as const;

function AnimatedStepMedia({ Player, src, poster }: { Player: LottiePlayer | null; src: string; poster: string }) {
    const [dotLottie, setDotLottie] = useState<DotLottie | null>(null);
    const [animationReady, setAnimationReady] = useState(false);

    useEffect(() => {
        if (!dotLottie) {
            return;
        }

        const markReady = () => setAnimationReady(true);
        dotLottie.addEventListener('load', markReady);
        dotLottie.addEventListener('render', markReady);

        return () => {
            dotLottie.removeEventListener('load', markReady);
            dotLottie.removeEventListener('render', markReady);
        };
    }, [dotLottie]);

    return (
        <div className="relative z-10 h-full w-full">
            {Player ? (
                <Player
                    src={src}
                    loop
                    autoplay
                    dotLottieRefCallback={setDotLottie}
                    className={`h-full w-full transition-opacity duration-200 ${animationReady ? 'opacity-100' : 'opacity-0'}`}
                />
            ) : null}
            <img
                src={poster}
                alt=""
                width={320}
                height={224}
                decoding="async"
                loading="lazy"
                className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-200 ${
                    animationReady ? 'pointer-events-none opacity-0' : 'opacity-100'
                }`}
            />
        </div>
    );
}

export default function HowItWorks() {
    const { t } = useTranslation();
    const route = useLocalizedRoute();
    const asset = useAsset();
    const sectionRef = useRef<HTMLDivElement>(null);
    const [Player, setPlayer] = useState<LottiePlayer | null>(null);

    useEffect(() => {
        const section = sectionRef.current;

        if (!section) {
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry?.isIntersecting) {
                    return;
                }

                observer.disconnect();

                if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                    return;
                }

                void import('@lottiefiles/dotlottie-react').then((module) => {
                    setPlayer(() => module.DotLottieReact);
                });
            },
            { rootMargin: '200px' },
        );

        observer.observe(section);

        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={sectionRef}
            className="relative isolate overflow-hidden bg-linear-to-b from-gray-50 to-white py-12 sm:py-16 lg:py-24 dark:bg-linear-to-b dark:from-gray-950 dark:to-gray-900"
            id="how-it-works"
        >
            <div className="mx-auto max-w-2xl px-6 lg:max-w-7xl lg:px-8">
                <h2 className="text-center text-sm font-semibold tracking-wide text-hs-blue uppercase dark:text-hs-green-light">
                    {t('home.howItWorks.title')}
                </h2>
                <h2 className="mx-auto mt-4 max-w-2xl text-center text-4xl font-semibold tracking-tight text-balance text-headline sm:text-5xl">
                    {t('home.howItWorks.subtitle')}
                </h2>
                <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-10">
                    {STEPS.map((step) => (
                        <div
                            key={step.step}
                            className="rounded-2xl border border-gray-200 bg-white p-8 shadow-xs ring-0 dark:border-gray-700/50 dark:bg-gray-800"
                        >
                            <div className="flex flex-col items-center">
                                <div className="relative mb-4 flex h-56 w-full max-w-xs items-center justify-center">
                                    <div className="absolute h-64 w-64 rounded-full bg-gray-700 opacity-0 transition-opacity duration-200 dark:opacity-30"></div>
                                    <AnimatedStepMedia Player={Player} src={asset(step.src)} poster={asset(step.poster)} />
                                </div>
                                <div className="mb-3 flex items-center justify-center gap-3">
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-hs-blue text-sm font-bold text-white dark:bg-hs-blue-light dark:text-white">
                                        {step.step}
                                    </div>
                                    <h3 className="text-center text-base leading-relaxed font-semibold text-headline">
                                        {t(`home.howItWorks.step${step.step}.title`)}
                                    </h3>
                                </div>
                                <p className="text-center text-base leading-relaxed text-paragraph">
                                    {t(`home.howItWorks.step${step.step}.description`)}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-16 flex items-center justify-center">
                    <Button
                        variant="default"
                        size="lg"
                        className="text-md transform px-8 py-3 font-medium shadow-lg transition-all duration-200 hover:scale-103 hover:shadow-xl"
                        asChild
                    >
                        <EntryLink href={route('register')}>{t('home.howItWorks.cta')}</EntryLink>
                    </Button>
                </div>
            </div>
        </div>
    );
}
